import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Star, Zap, Diamond } from "lucide-react";
import type { ServiceTier } from "@/data/serviceTiers";

interface ServiceTierCardProps {
  tier: ServiceTier;
  variant?: "standard" | "promo" | "minimal";
  customBadgeText?: string;
  onSelect?: (tierId: string) => void;
  className?: string;
}

export const ServiceTierCard = ({
  tier,
  variant = "standard",
  customBadgeText,
  onSelect,
  className = ""
}: ServiceTierCardProps) => {
  const features = variant === "promo" ? tier.featuresAbbreviated : tier.featuresStandard;
  
  const getFeatureIcon = (featureIndex: number) => {
    if (featureIndex === 0 && tier.tier > 0) {
      return tier.tier === 1 ? Star : tier.tier === 2 ? Zap : Diamond;
    }
    return CheckCircle;
  };
  
  const getIconSize = (featureIndex: number) => {
    if (featureIndex === 0 && tier.tier > 0) {
      return tier.tier === 1 ? "w-4 h-4" : "w-5 h-5";
    }
    return tier.tier === 0 ? "w-3 h-3" : "w-4 h-4";
  };
  
  const getIconColor = (featureIndex: number) => {
    if (featureIndex === 0 && tier.tier > 0) {
      return tier.tier === 1 ? "text-primary" : "text-gold";
    }
    return tier.tier === 0 
      ? "text-muted-foreground" 
      : tier.tier === 1 
      ? "text-accent" 
      : tier.tier === 2 
      ? "text-primary" 
      : "text-gold";
  };

  return (
    <Card 
      className={`relative bg-gradient-to-br ${tier.gradient} backdrop-blur border-primary/20 hover:border-primary/40 transition-all duration-300 ${variant === "standard" ? "hover:scale-105" : ""} ${className}`}
    >
      {/* Badges */}
      {customBadgeText && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-gold-foreground z-10">
          {customBadgeText}
        </Badge>
      )}
      {tier.popular && !customBadgeText && (
        <div className="absolute top-4 right-4 z-10">
          <Badge className="bg-gold text-gold-foreground font-semibold">
            Most Popular
          </Badge>
        </div>
      )}
      {tier.popular && customBadgeText && (
        <Badge className="absolute top-2 right-2 bg-primary text-primary-foreground z-10">
          Most Popular
        </Badge>
      )}

      <CardHeader className="text-center pb-4 pt-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
          <tier.icon className="w-8 h-8 text-primary-foreground" />
        </div>
        <CardTitle className="text-2xl font-bold text-primary mb-2">
          {tier.name}
        </CardTitle>
        {variant === "promo" && tier.regularPrice && (
          <p className="text-sm text-muted-foreground mb-1">White-Glove Service</p>
        )}
        <div className="text-3xl font-bold text-gold">
          {tier.displayPrice}
          <span className="text-sm font-normal text-muted-foreground">/{tier.period}</span>
        </div>
        {tier.regularPrice && (
          <div className="text-sm text-muted-foreground mt-1">
            Regular: <span className="line-through">{tier.regularPrice}</span> value
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        {variant === "standard" && (
          <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
            {tier.shortDescription}
          </p>
        )}
        
        <ul className={`space-y-3 ${variant === "promo" ? "mb-4" : "mb-8"}`}>
          {features.map((feature, featureIndex) => {
            const FeatureIcon = getFeatureIcon(featureIndex);
            
            return (
              <li key={featureIndex} className="flex items-start gap-3 text-sm">
                <FeatureIcon 
                  className={`${getIconSize(featureIndex)} ${getIconColor(featureIndex)} mt-0.5 flex-shrink-0`} 
                />
                <span className={`${featureIndex === 0 && tier.tier > 0 ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                  {feature}
                </span>
              </li>
            );
          })}
        </ul>

        {onSelect && (
          <Button 
            className={`w-full ${
              tier.popular 
                ? 'bg-gold hover:bg-gold/90 text-gold-foreground' 
                : 'bg-primary hover:bg-primary/90'
            } font-semibold`}
            onClick={() => onSelect(tier.id)}
          >
            Choose {tier.name}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
